import { CUSTOM_CAT, type CustomSymbol, type Prim } from './custom';
import { t as $t, tk } from './i18n';


/*
 * DXF → symbol candidates. Reads ASCII DXF (R12 … 2018): block definitions
 * become one candidate each; drawings laid out loose in model space are split
 * into clusters of touching entities. Supported entities: LINE, LWPOLYLINE
 * (with bulges), POLYLINE, CIRCLE, ARC, ELLIPSE, SPLINE (control polygon),
 * SOLID/TRACE, TEXT, MTEXT, ATTDEF, INSERT (nested, scaled, rotated).
 */

type P = [number, number];
export type DxfGeo = Geo;
/** Colour ("#rrggbb", resolved from the entity, its block or its layer) and whether the shape is filled (SOLID). */
/** blk: the name of the outermost block the shape was inserted with (a clue to what device it draws) */
interface Look { col?: string; fill?: boolean; blk?: string }
type Geo =
  | ({ k: 'line'; pts: P[]; closed?: boolean; dash?: boolean } & Look)
  | ({ k: 'circle'; c: P; r: number } & Look)
  /** ha / va: DXF horizontal (72) and vertical (73) alignment of `p` */
  | ({ k: 'text'; p: P; h: number; text: string; rot: number; bold?: boolean; ha?: number; va?: number } & Look);

/** What explode needs to give geometry its colour and texts their weight; left out when only shapes matter. */
interface Paint { layers: Map<string, string>; bold: Map<string, boolean>; byBlock?: string }

/** AutoCAD colour index → "#rrggbb" (7, white on screen, is black on paper). */
export function aciColour(n: number): string {
  const base: Record<number, string> = { 1: '#ff0000', 2: '#ffff00', 3: '#00ff00', 4: '#00ffff', 5: '#0000ff', 6: '#ff00ff', 7: '#000000', 8: '#808080', 9: '#c0c0c0' };
  if (base[n]) return base[n];
  const hex = (r: number, g: number, b: number) => '#' + [r, g, b].map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('');
  if (n >= 250 && n <= 255) {
    const v = [51, 91, 132, 173, 214, 255][n - 250];
    return hex(v, v, v);
  }
  if (n < 10 || n > 249) return '#000000';
  // 10…249: 24 hues in steps of 15°, each in 10 shades (even = full, odd = pale; brightness falls every two)
  const hue = Math.floor((n - 10) / 10) * 15, k = (n - 10) % 10, v = [1, 1, 0.8, 0.8, 0.6, 0.6, 0.5, 0.5, 0.3, 0.3][k], sat = k % 2 ? 0.5 : 1;
  const f = (m: number) => {
    const x = (m + hue / 60) % 6;
    return v * (1 - sat * Math.max(0, Math.min(1, Math.min(x, 4 - x))));
  };
  return hex(f(5) * 255, f(3) * 255, f(1) * 255);
}

interface Ent {
  type: string;
  g: [number, string][];
}

export interface DxfCandidate {
  /** block name or "Çizim n" for model-space clusters */
  name: string;
  source: 'block' | 'model';
  geo: Geo[];
  /** bounding box in DXF units, y up */
  bb: [number, number, number, number];
}

export interface DxfFile {
  version: string;
  units: string;
  candidates: DxfCandidate[];
  warnings: string[];
}

/* ---------------- decoding ---------------- */

/** Decodes DXF bytes: UTF-8 from AutoCAD 2007 on, otherwise the file's $DWGCODEPAGE. */
export function decodeDxf(bytes: Uint8Array): string {
  const head = new TextDecoder('latin1').decode(bytes.subarray(0, Math.min(bytes.length, 20000)));
  const ver = /\$ACADVER\s*\r?\n\s*1\s*\r?\n\s*(AC\d+)/.exec(head)?.[1] ?? 'AC1009';
  if (ver >= 'AC1021') return new TextDecoder('utf-8').decode(bytes);
  const cp = /\$DWGCODEPAGE\s*\r?\n\s*3\s*\r?\n\s*(\S+)/.exec(head)?.[1]?.toUpperCase() ?? 'ANSI_1252';
  const n = /(\d{3,4})/.exec(cp)?.[1];
  try {
    return new TextDecoder(n ? `windows-${n}` : 'windows-1252').decode(bytes);
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}

/** Replaces \U+XXXX escapes and strips MTEXT formatting. */
function cleanText(s: string, mtext: boolean): string {
  s = s.replace(/\\U\+([0-9A-Fa-f]{4})/g, (_, h) => String.fromCharCode(parseInt(h, 16)));
  s = s.replace(/%%[cC]/g, 'Ø').replace(/%%[dD]/g, '°').replace(/%%[pP]/g, '±').replace(/%%[uUoO]/g, '');
  if (mtext) {
    s = s
      // paragraph formatting (\pxqc; \pt15;) first: \p would otherwise read as a line break
      .replace(/\\p[^;\\{}]*;/g, '')
      .replace(/\\[PpNn]/g, ' ')
      .replace(/\\[A-Za-z][^;\\{}]*;/g, '')
      .replace(/\\[LlOoKk~]/g, '')
      .replace(/[{}]/g, '')
      .replace(/\\\\/g, '\\');
  }
  return s.replace(/\s+/g, ' ').trim();
}

/* ---------------- tokenising ---------------- */

function sections(text: string): { header: Map<string, string>; blocks: Map<string, { base: P; ents: Ent[] }>; model: Ent[]; paint: Paint } {
  const lines = text.split(/\r?\n/);
  const pairs: [number, string][] = [];
  for (let i = 0; i + 1 < lines.length; i += 2) {
    const c = parseInt(lines[i].trim(), 10);
    if (Number.isNaN(c)) throw new Error($t("Dosya ASCII DXF değil ya da bozuk (ikili DXF desteklenmiyor; AutoCAD'de \"DXF ASCII\" olarak kaydedin)."));
    pairs.push([c, lines[i + 1].replace(/\s+$/, '')]);
  }
  const header = new Map<string, string>();
  const blocks = new Map<string, { base: P; ents: Ent[] }>();
  const paint: Paint = { layers: new Map(), bold: new Map() };
  const model: Ent[] = [];
  let i = 0;
  const readEnts = (stop: (t: string) => boolean): Ent[] => {
    const out: Ent[] = [];
    while (i < pairs.length && pairs[i][0] === 0 && !stop(pairs[i][1])) {
      const e: Ent = { type: pairs[i][1], g: [] };
      i++;
      while (i < pairs.length && pairs[i][0] !== 0) e.g.push(pairs[i++]);
      out.push(e);
    }
    return out;
  };
  while (i < pairs.length) {
    if (pairs[i][0] === 0 && pairs[i][1] === 'SECTION') {
      const name = pairs[i + 1]?.[1];
      i += 2;
      if (name === 'HEADER') {
        let key = '';
        while (i < pairs.length && !(pairs[i][0] === 0 && pairs[i][1] === 'ENDSEC')) {
          if (pairs[i][0] === 9) key = pairs[i][1];
          else if (key && !header.has(key)) header.set(key, pairs[i][1]);
          i++;
        }
      } else if (name === 'BLOCKS') {
        while (i < pairs.length && !(pairs[i][0] === 0 && pairs[i][1] === 'ENDSEC')) {
          if (pairs[i][0] === 0 && pairs[i][1] === 'BLOCK') {
            i++;
            let bname = '', bx = 0, by = 0;
            while (i < pairs.length && pairs[i][0] !== 0) {
              const [c, v] = pairs[i++];
              if (c === 2) bname = v;
              if (c === 10) bx = +v;
              if (c === 20) by = +v;
            }
            const ents = readEnts((t) => t === 'ENDBLK');
            blocks.set(bname, { base: [bx, by], ents });
          } else i++;
        }
      } else if (name === 'ENTITIES') {
        model.push(...readEnts((t) => t === 'ENDSEC'));
      } else if (name === 'TABLES') {
        // layer colours and text styles (bold fonts mark wire numbers in EPLAN-style drawings)
        while (i < pairs.length && !(pairs[i][0] === 0 && pairs[i][1] === 'ENDSEC')) {
          if (pairs[i][0] === 0 && (pairs[i][1] === 'LAYER' || pairs[i][1] === 'STYLE')) {
            const e: Ent = { type: pairs[i][1], g: [] };
            i++;
            while (i < pairs.length && pairs[i][0] !== 0) e.g.push(pairs[i++]);
            if (e.type === 'LAYER') paint.layers.set(str(e, 2), colourOf(e) || '#000000');
            else paint.bold.set(str(e, 2).toUpperCase(), /bd|bold|black|heavy|kalin/i.test(str(e, 3) + ' ' + str(e, 2)));
          } else i++;
        }
      } else {
        while (i < pairs.length && !(pairs[i][0] === 0 && pairs[i][1] === 'ENDSEC')) i++;
      }
    } else i++;
  }
  return { header, blocks, model, paint };
}

/** The entity's own colour: true colour (420) or colour index (62); undefined for by-layer, 0 for by-block. */
function colourOf(e: Ent): string | 0 | undefined {
  const tc = e.g.find((x) => x[0] === 420);
  if (tc) return '#' + (parseInt(tc[1], 10) & 0xffffff).toString(16).padStart(6, '0');
  const ci = e.g.find((x) => x[0] === 62);
  if (!ci) return undefined;
  const n = Math.abs(parseInt(ci[1], 10));
  return n === 0 ? 0 : n === 256 ? undefined : aciColour(n);
}

/* ---------------- geometry ---------------- */

type M = [number, number, number, number, number, number]; // affine: x' = a x + c y + e ; y' = b x + d y + f
const ID: M = [1, 0, 0, 1, 0, 0];
const mul = (m: M, n: M): M => [
  m[0] * n[0] + m[2] * n[1], m[1] * n[0] + m[3] * n[1],
  m[0] * n[2] + m[2] * n[3], m[1] * n[2] + m[3] * n[3],
  m[0] * n[4] + m[2] * n[5] + m[4], m[1] * n[4] + m[3] * n[5] + m[5],
];
const ap = (m: M, p: P): P => [m[0] * p[0] + m[2] * p[1] + m[4], m[1] * p[0] + m[3] * p[1] + m[5]];
const scaleOf = (m: M) => Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2]));
const rotOf = (m: M) => (Math.atan2(m[1], m[0]) * 180) / Math.PI;

function arcPts(c: P, r: number, a0: number, a1: number): P[] {
  while (a1 <= a0) a1 += 360;
  const n = Math.max(4, Math.ceil((a1 - a0) / 10));
  return Array.from({ length: n + 1 }, (_, k) => {
    const t = ((a0 + ((a1 - a0) * k) / n) * Math.PI) / 180;
    return [c[0] + r * Math.cos(t), c[1] + r * Math.sin(t)] as P;
  });
}

/** Expands a polyline with bulges (arc segments) into points. */
function bulgePts(v: { p: P; b: number }[], closed: boolean): P[] {
  const out: P[] = [];
  const n = v.length;
  for (let i = 0; i < (closed ? n : n - 1); i++) {
    const a = v[i], b = v[(i + 1) % n];
    out.push(a.p);
    if (Math.abs(a.b) > 1e-9) {
      const th = 4 * Math.atan(a.b), dx = b.p[0] - a.p[0], dy = b.p[1] - a.p[1], d = Math.hypot(dx, dy);
      if (d > 0) {
        const r = d / (2 * Math.sin(th / 2)), mx = (a.p[0] + b.p[0]) / 2, my = (a.p[1] + b.p[1]) / 2;
        const h = r * Math.cos(th / 2), nx = -dy / d, ny = dx / d;
        const c: P = [mx + nx * h, my + ny * h];
        const s = Math.atan2(a.p[1] - c[1], a.p[0] - c[0]);
        const steps = Math.max(3, Math.ceil(Math.abs(th) / (Math.PI / 18)));
        for (let k = 1; k < steps; k++) {
          const t = s + (th * k) / steps;
          out.push([c[0] + Math.abs(r) * Math.cos(t), c[1] + Math.abs(r) * Math.sin(t)]);
        }
      }
    }
  }
  if (!closed && n) out.push(v[n - 1].p);
  return out;
}

const num = (e: Ent, c: number, d = 0) => {
  const v = e.g.find((x) => x[0] === c);
  return v ? parseFloat(v[1]) : d;
};
const str = (e: Ent, c: number) => e.g.find((x) => x[0] === c)?.[1] ?? '';

function explode(ents: Ent[], m: M, blocks: Map<string, { base: P; ents: Ent[] }>, depth: number, warn: Set<string>, paint?: Paint): Geo[] {
  const out: Geo[] = [];
  for (let i = 0; i < ents.length; i++) {
    const e = ents[i];
    const dash = /DASH|HIDDEN|CENTER|KESIK/i.test(str(e, 6));
    // colour: own, else by block (inside a block on layer 0 or "by block"), else the layer's
    let col: string | undefined;
    if (paint) {
      const own = colourOf(e), layer = str(e, 8) || '0';
      col = own === 0 || (own === undefined && layer === '0' && paint.byBlock) ? paint.byBlock ?? paint.layers.get(layer) : own ?? paint.layers.get(layer) ?? '#000000';
    }
    const len0 = out.length;
    switch (e.type) {
      case 'LINE':
        out.push({ k: 'line', pts: [ap(m, [num(e, 10), num(e, 20)]), ap(m, [num(e, 11), num(e, 21)])], dash });
        break;
      case 'LWPOLYLINE': {
        const v: { p: P; b: number }[] = [];
        for (const [c, val] of e.g) {
          if (c === 10) v.push({ p: [+val, 0], b: 0 });
          else if (c === 20 && v.length) v[v.length - 1].p[1] = +val;
          else if (c === 42 && v.length) v[v.length - 1].b = +val;
        }
        const closed = (num(e, 70) & 1) === 1;
        if (v.length >= 2) out.push({ k: 'line', pts: bulgePts(v, closed).map((p) => ap(m, p)), closed, dash });
        break;
      }
      case 'POLYLINE': {
        const closed = (num(e, 70) & 1) === 1;
        const v: { p: P; b: number }[] = [];
        while (ents[i + 1]?.type === 'VERTEX') {
          const vx = ents[++i];
          v.push({ p: [num(vx, 10), num(vx, 20)], b: num(vx, 42) });
        }
        if (ents[i + 1]?.type === 'SEQEND') i++;
        if (v.length >= 2) out.push({ k: 'line', pts: bulgePts(v, closed).map((p) => ap(m, p)), closed, dash });
        break;
      }
      case 'CIRCLE': {
        const c = ap(m, [num(e, 10), num(e, 20)]), r = num(e, 40) * scaleOf(m);
        out.push({ k: 'circle', c, r });
        break;
      }
      case 'ARC': {
        const pts = arcPts([num(e, 10), num(e, 20)], num(e, 40), num(e, 50), num(e, 51));
        out.push({ k: 'line', pts: pts.map((p) => ap(m, p)), dash });
        break;
      }
      case 'ELLIPSE': {
        const c: P = [num(e, 10), num(e, 20)], mj: P = [num(e, 11), num(e, 21)], ratio = num(e, 40, 1);
        let t0 = num(e, 41, 0), t1 = num(e, 42, Math.PI * 2);
        if (t1 <= t0) t1 += Math.PI * 2;
        const mn: P = [-mj[1] * ratio, mj[0] * ratio];
        const n = Math.max(8, Math.ceil((t1 - t0) / (Math.PI / 18)));
        const pts = Array.from({ length: n + 1 }, (_, k) => {
          const t = t0 + ((t1 - t0) * k) / n;
          return ap(m, [c[0] + mj[0] * Math.cos(t) + mn[0] * Math.sin(t), c[1] + mj[1] * Math.cos(t) + mn[1] * Math.sin(t)]);
        });
        out.push({ k: 'line', pts, dash });
        break;
      }
      case 'SPLINE': {
        const pts: P[] = [];
        const fit = e.g.filter((x) => x[0] === 11).length > 1;
        for (const [c, val] of e.g) {
          if (c === (fit ? 11 : 10)) pts.push([+val, 0]);
          else if (c === (fit ? 21 : 20) && pts.length) pts[pts.length - 1][1] = +val;
        }
        if (pts.length >= 2) out.push({ k: 'line', pts: pts.map((p) => ap(m, p)), dash });
        warn.add($t("SPLINE eğrileri kontrol noktalarıyla yaklaşık çizildi."));
        break;
      }
      case 'SOLID':
      case 'TRACE': {
        const q: P[] = [[num(e, 10), num(e, 20)], [num(e, 11), num(e, 21)], [num(e, 13), num(e, 23)], [num(e, 12), num(e, 22)]];
        out.push({ k: 'line', pts: q.map((p) => ap(m, p)), closed: true, fill: true });
        break;
      }
      case 'TEXT':
      case 'ATTDEF':
      case 'MTEXT': {
        const raw = e.type === 'MTEXT' ? e.g.filter((x) => x[0] === 3 || x[0] === 1).map((x) => x[1]).join('') : e.type === 'ATTDEF' ? str(e, 2) || str(e, 1) : str(e, 1);
        const text = cleanText(raw, e.type === 'MTEXT');
        if (!text) break;
        const h = num(e, 40, 2.5) * scaleOf(m);
        const aligned = e.type === 'TEXT' && (num(e, 72) || num(e, 73));
        const p = ap(m, aligned ? [num(e, 11), num(e, 21)] : [num(e, 10), num(e, 20)]);
        // MTEXT: attachment point 1…9 (top-left … bottom-right) as horizontal / vertical alignment, bold from its font code
        const at = e.type === 'MTEXT' ? num(e, 71, 1) : 0;
        const bold = (paint?.bold.get((str(e, 7) || 'STANDARD').toUpperCase()) ?? false) || (e.type === 'MTEXT' && /\\f[^;]*\|b1/.test(raw));
        out.push({
          // MTEXT: the direction vector (11/21), when given, wins over the rotation angle
          k: 'text', p, h, text, rot: (e.type === 'MTEXT' && (num(e, 11) || num(e, 21)) ? (Math.atan2(num(e, 21), num(e, 11)) * 180) / Math.PI : num(e, 50)) + rotOf(m), bold,
          ha: e.type === 'TEXT' ? num(e, 72) : at ? (at - 1) % 3 : 0,
          va: e.type === 'TEXT' ? num(e, 73) : at ? [3, 2, 1][Math.floor((at - 1) / 3)] : 0,
        });
        break;
      }
      case 'INSERT': {
        const blk = blocks.get(str(e, 2));
        if (!blk || depth > 8) break;
        const sx = num(e, 41, 1), sy = num(e, 42, 1), a = (num(e, 50) * Math.PI) / 180;
        const cs = Math.cos(a), sn = Math.sin(a);
        const t: M = [cs * sx, sn * sx, -sn * sy, cs * sy, num(e, 10), num(e, 20)];
        const inner = mul(mul(m, t), [1, 0, 0, 1, -blk.base[0], -blk.base[1]]);
        const kids = explode(blk.ents, inner, blocks, depth + 1, warn, paint && { ...paint, byBlock: col });
        if (depth === 0) for (const g of kids) g.blk = str(e, 2);
        out.push(...kids);
        break;
      }
      case 'HATCH':
      case 'DIMENSION':
      case 'IMAGE':
      case 'VIEWPORT':
        warn.add($t("{0} öğeleri atlandı.", e.type));
        break;
    }
    if (col && e.type !== 'INSERT') for (let j = len0; j < out.length; j++) out[j].col = col;
  }
  return out;
}

/** The whole model space, blocks exploded, with colours and text weights; for converting drawings. */
export function dxfDrawing(text: string): { geo: Geo[]; warnings: string[]; mm: number } {
  const { header, blocks, model, paint } = sections(text);
  const warn = new Set<string>();
  const geo = explode(model, ID, blocks, 0, warn, paint);
  // drawing units → mm (inches and the like; unitless and mm stay)
  const mm = ({ '1': 25.4, '2': 304.8, '5': 10, '6': 1000 } as Record<string, number>)[header.get('$INSUNITS') ?? ''] ?? 1;
  return { geo, warnings: [...warn], mm };
}

function bbox(geo: Geo[]): [number, number, number, number] {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  const add = (p: P, r = 0) => {
    x0 = Math.min(x0, p[0] - r);
    y0 = Math.min(y0, p[1] - r);
    x1 = Math.max(x1, p[0] + r);
    y1 = Math.max(y1, p[1] + r);
  };
  for (const g of geo) {
    if (g.k === 'line') g.pts.forEach((p) => add(p));
    else if (g.k === 'circle') add(g.c, g.r);
    else {
      add(g.p);
      add([g.p[0] + g.text.length * g.h * 0.6, g.p[1] + g.h]);
    }
  }
  return [x0, y0, x1, y1];
}

/** Groups loose model-space geometry into separate drawings by touching bounding boxes. */
function cluster(geo: Geo[], gapRatio = 0.01): Geo[][] {
  if (!geo.length) return [];
  const all = bbox(geo), gap = Math.max(all[2] - all[0], all[3] - all[1]) * gapRatio;
  const boxes = geo.map((g) => bbox([g]));
  const parent = geo.map((_, i) => i);
  const find = (i: number): number => {
    // iterative with path compression: a long chain in a big drawing must not overflow the stack
    let r = i;
    while (parent[r] !== r) r = parent[r];
    while (parent[i] !== r) [parent[i], i] = [r, parent[i]];
    return r;
  };
  const order = boxes.map((_, i) => i).sort((a, b) => boxes[a][0] - boxes[b][0]);
  for (let a = 0; a < order.length; a++) {
    const A = boxes[order[a]];
    for (let b = a + 1; b < order.length; b++) {
      const B = boxes[order[b]];
      if (B[0] > A[2] + gap) break;
      if (B[1] <= A[3] + gap && B[3] >= A[1] - gap) parent[find(order[a])] = find(order[b]);
    }
  }
  const groups = new Map<number, Geo[]>();
  geo.forEach((g, i) => (groups.get(find(i)) ?? groups.set(find(i), []).get(find(i))!).push(g));
  return [...groups.values()];
}

const UNITS: Record<string, string> = { '0': tk('birimsiz'), '1': tk("inç"), '2': 'feet', '4': 'mm', '5': 'cm', '6': 'm' };

export function parseDxf(text: string): DxfFile {
  const { header, blocks, model } = sections(text);
  const warn = new Set<string>();
  const candidates: DxfCandidate[] = [];
  const used = new Set<string>();
  for (const e of model) if (e.type === 'INSERT') used.add(str(e, 2));
  for (const [name, b] of blocks) {
    if (name.startsWith('*') || /^(A\$|_)/.test(name)) continue; // model/paper space, anonymous and helper blocks
    const geo = explode(b.ents, [1, 0, 0, 1, -b.base[0], -b.base[1]], blocks, 0, warn);
    if (geo.length) candidates.push({ name, source: 'block', geo, bb: bbox(geo) });
  }
  // loose drawings in model space (not the ones that are just block insertions)
  const loose = explode(model.filter((e) => e.type !== 'INSERT' || !candidates.some((c) => c.name === str(e, 2))), ID, blocks, 0, warn);
  cluster(loose).forEach((geo, i) => {
    if (geo.length) candidates.push({ name: $t("Çizim {0}", i + 1), source: 'model', geo, bb: bbox(geo) });
  });
  // model-space clusters: sort top-to-bottom, left-to-right like the sheet is read
  candidates.sort((a, b) => (a.source === b.source ? (a.source === 'model' ? b.bb[3] - a.bb[3] || a.bb[0] - b.bb[0] : a.name.localeCompare(b.name)) : a.source === 'block' ? -1 : 1));
  let m = 0;
  for (const c of candidates) if (c.source === 'model') c.name = $t("Çizim {0}", ++m);
  return { version: header.get('$ACADVER') ?? '?', units: $t(UNITS[header.get('$INSUNITS') ?? ''] ?? '') || $t("belirtilmemiş"), candidates, warnings: [...warn] };
}

/**
 * The whole model space with block insertions exploded, split into sheets:
 * drawings laid out side by side become one sheet each; small loose bits go
 * to the nearest sheet. Sheets are ordered top-to-bottom, left-to-right.
 */
export function dxfSheets(text: string): { sheets: { geo: Geo[]; bb: [number, number, number, number] }[]; warnings: string[] } {
  const { blocks, model } = sections(text);
  const warn = new Set<string>();
  return { sheets: sheetsOf(explode(model, ID, blocks, 0, warn)), warnings: [...warn] };
}

/** Separate drawings laid out side by side (sheets without a frame we can recognise); see dxfSheets. */
export function sheetsOf(geo: Geo[]): { geo: Geo[]; bb: [number, number, number, number] }[] {
  const groups = cluster(geo, 0.005).map((g) => ({ geo: g, bb: bbox(g) }));
  const size = (b: number[]) => (b[2] - b[0] + 1) * (b[3] - b[1] + 1);
  const big = Math.max(0, ...groups.map((g) => size(g.bb)));
  const sheets = groups.filter((g) => size(g.bb) >= big / 4);
  const centre = (b: number[]) => [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2];
  for (const g of groups) {
    if (sheets.includes(g)) continue;
    const [x, y] = centre(g.bb);
    const near = sheets.reduce((a, s) => (Math.hypot(centre(s.bb)[0] - x, centre(s.bb)[1] - y) < Math.hypot(centre(a.bb)[0] - x, centre(a.bb)[1] - y) ? s : a));
    near.geo.push(...g.geo);
    near.bb = bbox(near.geo);
  }
  // rows of sheets from the top (DXF y up), then left to right
  const h = Math.max(1, ...sheets.map((s) => s.bb[3] - s.bb[1]));
  sheets.sort((a, b) => Math.round((b.bb[3] - a.bb[3]) / (h / 2)) || a.bb[0] - b.bb[0]);
  return sheets;
}

export interface DxfToSymbolOptions {
  /** mm per DXF unit; 'fit' scales the drawing to `fitHeight` mm */
  scale: number | 'fit';
  fitHeight?: number;
  name?: string;
  cat?: string;
  prefix?: string;
}

/**
 * Converts a candidate to an (unsaved) custom symbol: y flipped to screen
 * orientation, scaled, origin at the top centre snapped to the 4 mm grid.
 * Pins are left for the user to place.
 */
export function candidateToSymbol(c: DxfCandidate, o: DxfToSymbolOptions): Omit<CustomSymbol, 'key'> {
  const [x0, y0, x1, y1] = c.bb;
  const h = Math.max(y1 - y0, 1e-9), w = Math.max(x1 - x0, 1e-9);
  let k = o.scale === 'fit' ? (o.fitHeight ?? 16) / Math.max(h, w * 0.5) : o.scale;
  // keep within the symbol coordinate limits
  k = Math.min(k, 380 / Math.max(w, h));
  const cx = (x0 + x1) / 2;
  const T = (p: P): [number, number] => [r2((p[0] - cx) * k), r2((y1 - p[1]) * k)];
  const prims: Prim[] = [];
  for (const g of c.geo) {
    if (prims.length >= 500) break;
    if (g.k === 'line') {
      let pts = g.pts.map(T);
      if (g.closed) pts = [...pts, pts[0]];
      pts = pts.filter((p, i) => i === 0 || p[0] !== pts[i - 1][0] || p[1] !== pts[i - 1][1]);
      if (pts.length >= 2) prims.push({ k: 'line', pts: pts.slice(0, 200), ...(g.dash ? { dash: true } : {}) });
    } else if (g.k === 'circle') {
      const [x, y] = T(g.c);
      if (g.r * k >= 0.05) prims.push({ k: 'circle', x, y, r: r2(g.r * k) });
    } else {
      const size = r2(Math.min(20, Math.max(1, (g.h * k) / 0.72)));
      const [x, y] = T(g.p);
      prims.push({ k: 'text', x: r2(x + (g.text.length * size * 0.56) / 2), y, size, text: g.text.slice(0, 40) });
    }
  }
  return {
    name: (o.name ?? c.name).slice(0, 60) || $t("DXF sembolü"),
    cat: o.cat ?? CUSTOM_CAT,
    prefix: o.prefix ?? 'U',
    role: 'main',
    mirror: false,
    pins: [],
    pass: [],
    prims,
  };
}

const r2 = (v: number) => Math.round(v * 100) / 100;
